// Ask-and-capture widget (all sites). Answers from this site's own published articles with citations, then offers
// the next step: a lead form on the agency/finance sites, our own links elsewhere. Questions are logged server-side
// and feed the keyword planner, so every question makes the next article better.
import { useEffect, useRef, useState } from 'react';
import { MessageCircleQuestion, X, Send, Loader2 } from 'lucide-react';
import Link from '@/lib/siteLink';
import { currentSiteId, SITE_BRANDS } from '@/config/siteBrands';

const API = process.env.REACT_APP_BACKEND_URL || '';
const sessionId = () => {
  try {
    let s = sessionStorage.getItem('amm_ask_sid');
    if (!s) { s = Math.random().toString(36).slice(2); sessionStorage.setItem('amm_ask_sid', s); }
    return s;
  } catch { return ''; }
};

export default function AskWidget() {
  const site = currentSiteId();
  const brand = SITE_BRANDS[site] || SITE_BRANDS.auto;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [lead, setLead] = useState({ name: '', email: '', sent: false, sending: false });
  const inputRef = useRef(null);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);

  const ask = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 5 || busy) return;
    setBusy(true); setRes(null);
    try {
      const r = await fetch(`${API}/api/ask`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site, question: q.trim(), session_id: sessionId() }),
      });
      setRes(r.ok ? await r.json() : { answer: (await r.json().catch(() => ({}))).detail || 'Something went wrong. Try again in a moment.', sources: [] });
    } catch {
      setRes({ answer: 'Could not reach us just now. Try again in a moment.', sources: [] });
    } finally { setBusy(false); }
  };

  const sendLead = async (e) => {
    e.preventDefault();
    if (!lead.email.trim()) return;
    setLead((l) => ({ ...l, sending: true }));
    try {
      await fetch(`${API}/api/leads/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: lead.name || 'Ask widget', email: lead.email, site,
          source: res?.next_step?.source || `${site}-ask`,
          notes: `Asked: ${q.trim()}`,
        }),
      });
      setLead((l) => ({ ...l, sent: true, sending: false }));
    } catch { setLead((l) => ({ ...l, sending: false })); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="ask-widget-open"
        className="fixed right-5 z-[60] flex items-center gap-2 h-12 px-4 rounded-full shadow-lg text-white font-semibold text-sm"
        style={{ bottom: 'calc(1.25rem + var(--bottom-bar-h, 0px) + env(safe-area-inset-bottom, 0px))', background: brand.themeColor }}
        aria-label={`Ask ${brand.name} a question`}>
        <MessageCircleQuestion className="w-5 h-5" /> <span className="hidden sm:inline">Ask a question</span>
      </button>
    );
  }

  return (
    <div className="fixed right-5 z-[60] w-[min(92vw,26rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden" data-testid="ask-widget"
      style={{ bottom: 'calc(1.25rem + var(--bottom-bar-h, 0px) + env(safe-area-inset-bottom, 0px))' }}>
      <div className="flex items-center justify-between px-4 h-12 text-white" style={{ background: brand.themeColor }}>
        <p className="font-semibold text-sm">Ask {brand.name}</p>
        <button onClick={() => setOpen(false)} aria-label="Close"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-4 max-h-[60vh] overflow-y-auto">
        <p className="text-xs text-slate-500">Answered from what we have published, with links to the articles.</p>
        <form onSubmit={ask} className="mt-3 flex gap-2">
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask anything about this topic"
            className="flex-1 h-10 px-3 rounded-lg border border-slate-300 text-sm outline-none focus:border-slate-500" />
          <button type="submit" disabled={busy} className="h-10 w-10 rounded-lg text-white flex items-center justify-center disabled:opacity-50"
            style={{ background: brand.themeColor }} aria-label="Send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>

        {res && (
          <div className="mt-4">
            <p className="text-sm text-slate-800 whitespace-pre-line">{res.answer}</p>
            {res.sources?.length > 0 && (
              <ul className="mt-3 space-y-1">
                {res.sources.map((s, i) => (
                  <li key={s.url} className="text-sm">
                    <Link to={s.url} onClick={() => setOpen(false)} className="text-slate-900 underline decoration-slate-300 hover:decoration-slate-900">
                      [{i + 1}] {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {res.next_step && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-sm font-medium">{res.next_step.cta}</p>
                {res.next_step.lead && !lead.sent && (
                  <form onSubmit={sendLead} className="mt-2 flex flex-col sm:flex-row gap-2">
                    <input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Name"
                      className="h-10 px-3 rounded-lg border border-slate-300 text-sm sm:w-28" />
                    <input type="email" required value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })}
                      placeholder="Email" className="flex-1 h-10 px-3 rounded-lg border border-slate-300 text-sm" />
                    <button type="submit" disabled={lead.sending} className="h-10 px-4 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                      style={{ background: brand.themeColor }}>{lead.sending ? 'Sending…' : 'Send'}</button>
                  </form>
                )}
                {lead.sent && <p className="mt-2 text-sm text-emerald-700">Thanks, we'll be in touch.</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
